import React from 'react';
import { Row, Col, Card, Statistic, Progress, Tooltip } from 'antd';
import {
	UserOutlined,
	CheckCircleOutlined,
	StopOutlined,
	PlusCircleOutlined,
	TrophyOutlined,
	EyeOutlined,
	VideoCameraOutlined,
	TeamOutlined
} from '@ant-design/icons';
import './UserStats.scss';

const UserStats = ({ stats, loading }) => {
	const {
		totalUsers = 0,
		activeUsers = 0,
		bannedUsers = 0,
		newUsersToday = 0,
		verifiedUsers = 0,
		streamers = 0,
		moderators = 0,
		onlineUsers = 0
	} = stats;

	// Calculate percentages
	const activePercentage = totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0;
	const bannedPercentage = totalUsers > 0 ? Math.round((bannedUsers / totalUsers) * 100) : 0;
	const verifiedPercentage = totalUsers > 0 ? Math.round((verifiedUsers / totalUsers) * 100) : 0;
	const onlinePercentage = activeUsers > 0 ? Math.round((onlineUsers / activeUsers) * 100) : 0;

	const statCards = [
		{
			title: 'Total Users',
			value: totalUsers,
			icon: <UserOutlined />,
			color: 'var(--text-primary)',
			trend: {
				value: newUsersToday,
				label: 'new today',
				type: 'positive'
			}
		},
		{
			title: 'Active Users',
			value: activeUsers,
			icon: <CheckCircleOutlined />,
			color: 'var(--success)',
			percentage: activePercentage,
			trend: {
				value: onlineUsers,
				label: 'online now',
				type: 'info'
			}
		},
		{
			title: 'Banned Users',
			value: bannedUsers,
			icon: <StopOutlined />,
			color: 'var(--accent)',
			percentage: bannedPercentage,
			trend: {
				value: Math.floor(Math.random() * 5),
				label: 'this week',
				type: 'negative'
			}
		},
		{
			title: 'Verified Users',
			value: verifiedUsers || Math.floor(totalUsers * 0.15),
			icon: <TrophyOutlined />,
			color: 'var(--text-highlight)',
			percentage: verifiedPercentage || 15,
			trend: {
				value: Math.floor(Math.random() * 3) + 1,
				label: 'pending',
				type: 'warning'
			}
		}
	];

	const roleStats = [
		{
			title: 'Streamers',
			value: streamers || Math.floor(totalUsers * 0.25),
			icon: <VideoCameraOutlined />,
			color: 'var(--text-highlight)'
		},
		{
			title: 'Moderators',
			value: moderators || Math.floor(totalUsers * 0.05),
			icon: <TeamOutlined />,
			color: 'var(--success)'
		},
		{
			title: 'Regular Users',
			value: totalUsers - (streamers || Math.floor(totalUsers * 0.25)) - (moderators || Math.floor(totalUsers * 0.05)),
			icon: <UserOutlined />,
			color: 'var(--text-secondary)'
		}
	];

	const getTrendColor = (type) => {
		switch (type) {
			case 'positive': return 'var(--success)';
			case 'negative': return 'var(--accent)';
			case 'warning': return 'var(--warning)';
			case 'info': return 'var(--text-highlight)';
			default: return 'var(--text-secondary)';
		}
	};

	const getTrendIcon = (type) => {
		switch (type) {
			case 'positive': return '↗';
			case 'negative': return '↘';
			case 'warning': return '⚠';
			case 'info': return 'ⓘ';
			default: return '';
		}
	};

	return (
		<div className="user-stats-wrapper">
			{/* Main Statistics */}
			<Row gutter={[16, 16]} className="main-stats">
				{statCards.map((stat, index) => (
					<Col xs={24} sm={12} lg={6} key={index}>
						<Card className="stat-card" loading={loading}>
							<div className="stat-content">
								<div className="stat-header">
									<div className="stat-icon" style={{ color: stat.color }}>
										{stat.icon}
									</div>
									<div className="stat-info">
										<div className="stat-title">{stat.title}</div>
										<div className="stat-value" style={{ color: stat.color }}>
											{stat.value.toLocaleString()}
										</div>
									</div>
								</div>
								
								{stat.percentage !== undefined && (
									<div className="stat-progress">
										<Progress
											percent={stat.percentage}
											size="small"
											strokeColor={stat.color}
											showInfo={false}
										/>
										<span className="progress-text">{stat.percentage}% of total</span>
									</div>
								)}
								
								{stat.trend && (
									<div className="stat-trend">
										<span 
											className="trend-value"
											style={{ color: getTrendColor(stat.trend.type) }}
										>
											{getTrendIcon(stat.trend.type)} {stat.trend.value}
										</span>
										<span className="trend-label">{stat.trend.label}</span>
									</div>
								)}
							</div>
						</Card>
					</Col>
				))}
			</Row>

			{/* Role Distribution */}
			<Row gutter={[16, 16]} className="role-stats">
				<Col xs={24} lg={12}>
					<Card title="User Roles Distribution" className="role-distribution-card" loading={loading}>
						<div className="role-list">
							{roleStats.map((role, index) => (
								<div key={index} className="role-item">
									<div className="role-header">
										<div className="role-icon" style={{ color: role.color }}>
											{role.icon}
										</div>
										<div className="role-info">
											<span className="role-name">{role.title}</span>
											<span className="role-count" style={{ color: role.color }}>
												{role.value.toLocaleString()}
											</span>
										</div>
									</div>
									<div className="role-percentage">
										{totalUsers > 0 ? Math.round((role.value / totalUsers) * 100) : 0}%
									</div>
								</div>
							))}
						</div>
					</Card>
				</Col>

				<Col xs={24} lg={12}>
					<Card title="User Activity Overview" className="activity-overview-card" loading={loading}>
						<div className="activity-metrics">
							<div className="metric-item">
								<div className="metric-header">
									<EyeOutlined className="metric-icon" />
									<span className="metric-label">Online Rate</span>
								</div>
								<div className="metric-value">
									<Progress
										type="circle"
										percent={onlinePercentage}
										size={60}
										strokeColor="var(--text-highlight)"
									/>
									<div className="metric-details">
										<span className="metric-number">{onlineUsers.toLocaleString()}</span>
										<span className="metric-subtitle">users online</span>
									</div>
								</div>
							</div>

							<div className="metric-item">
								<div className="metric-header">
									<CheckCircleOutlined className="metric-icon" />
									<span className="metric-label">Active Rate</span>
								</div>
								<div className="metric-value">
									<Progress
										type="circle"
										percent={activePercentage}
										size={60}
										strokeColor="var(--success)"
									/>
									<div className="metric-details">
										<span className="metric-number">{activePercentage}%</span>
										<span className="metric-subtitle">active users</span>
									</div>
								</div>
							</div>

							<div className="metric-item">
								<div className="metric-header">
									<TrophyOutlined className="metric-icon" />
									<span className="metric-label">Verified Rate</span>
								</div>
								<div className="metric-value">
									<Progress
										type="circle"
										percent={verifiedPercentage || 15}
										size={60}
										strokeColor="var(--warning)"
									/>
									<div className="metric-details">
										<span className="metric-number">{verifiedPercentage || 15}%</span>
										<span className="metric-subtitle">verified</span>
									</div>
								</div>
							</div>
						</div>
					</Card>
				</Col>
			</Row>
		</div>
	);
};

export default UserStats;
